<template>
  <div class="l-newsPage__wrapper">
    <div class="l-newsPage__grid-tools">
      <div>
        {{ translations["Showing"] }} <span>{{ totalLoaded }}</span>
        {{ translations["articles"] }}
      </div>
      <div class="sorting">
        <div class="direction" @click="toggleDirection()">
          <img src="/images/sort.svg" />
        </div>
        <div class="type">
          <multiselect
            v-model="selectedSortOption"
            deselect-label="Can't remove this value"
            track-by="code"
            label="label"
            placeholder="Sort by..."
            :options="sortOptions"
            :searchable="false"
            :clear-on-select="false"
            :taggable="false"
            :show-labels="false"
          >
            <template slot="singleLabel" slot-scope="props">
              <span class="option__desc">
                <span class="option__title">{{ props.option.label }}</span>
              </span>
            </template>
          </multiselect>
        </div>
      </div>
    </div>

    <div class="l-newsPage__grid" id="grid">
      <slot name="articles"></slot>
    </div>

    <div
      class="l-newsPage__row l-newsPage__loadmore c-categoryGrid__loadMore"
      v-if="showLoadMore"
    >
      <div class="o-btn" @click="loadNextPage()" :class="{ loading: loading }">
        {{ buttonLabel }}

        <inline-loader></inline-loader>
      </div>
    </div>

    <div class="l-newsPage__row">
      <div class="l-newsPage__counter">
        {{ translations["Showing"] }} <span>{{ totalLoaded }}</span>
        {{ translations["of"] }} {{ totalProducts }}
        {{ translations["articles"] }}
      </div>
    </div>

    <transition name="fade">
      <div class="c-categoryGrid__loader" v-if="showLoader">
        <slot name="loader"></slot>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

import multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import InlineLoader from "./InlineLoader.vue";
import Dropdown from "./Dropdown.vue";

export default {
  name: "NewsGrid",
  components: {
    InlineLoader,
    vSelect,
    Dropdown,
    multiselect,
  },
  props: {
    page: {
      type: Number,
      default: 1,
    },
    loaded: {
      type: Number,
      default: 1,
    },
    total: {
      type: Number,
      default: 0,
    },
    results: {
      type: Number,
      default: 0,
    },
    batch: {
      type: Number,
      default: 0,
    },
    buttonLabel: {
      type: String,
      default: "",
    },
    loadedOption: {
      type: String,
      default: "title",
    },
    loadedDirection: {
      type: String,
      default: "asc",
    },
    showPrice: {
      type: Number,
      default: 0,
    },
    filterOptions: {
      type: Array,
    },
    translations: {
      type: Object,
    },
  },
  data: () => ({
    initial: true,
    showLoader: false,
    isFiltersVisible: false,
    loading: false,
    currentPage: 0,
    totalPages: 0,
    totalProducts: 0,
    totalLoaded: 0,
    url: "",
    queryParams: new URLSearchParams(window.location.search),
    sortDirection: "",
    sortOption: "",
    filters: {},
    searchTerm: "",
    selectedSortOption: null,
  }),
  watch: {
    selectedSortOption: function (newProperty, oldProperty) {
      if (this.initial || !oldProperty) {
        return true;
      }

      this.updateParam("sortBy", newProperty.code);
      this.removeParam("page");
      this.reloadProducts();
    },
  },
  computed: {
    showLoadMore: function () {
      if (this.currentPage >= this.totalPages) {
        return false;
      }
      return true;
    },
    sortOptions: function () {
        return [
          { label: "Title", code: "title" },
          { label: "Date", code: "dateCreated" },
        ];
    },
  },
  methods: {
    toggleDirection: function () {
      this.sortDirection = this.sortDirection == "asc" ? "desc" : "asc";

      this.updateParam("direction", this.sortDirection);
      this.removeParam("page");
      this.reloadProducts();
    },
    updateParam: function (param, value) {
      this.queryParams.set(param, value);
    },
    removeParam: function (param) {
      this.queryParams.delete(param);
    },
    filter: function () {
      this.queryParams.forEach((v, k) => {
        this.removeParam(k);
      });

      for (const [key, value] of Object.entries(this.filters)) {
        if (value.length) {
          let tmpVal = [];
          for (const [key2, option] of Object.entries(value)) {
            tmpVal.push(option.code);
          }
          this.updateParam(key, tmpVal);
        }

        if (!value.length) {
          this.removeParam(key);
        }
      }

      this.removeParam("page");
      this.search();
    },
    reloadProducts: function () {
      this.showLoader = true;
      this.load()
        .then((response) => {
          this.totalPages = response.headers.totalpages;
          this.totalProducts = response.headers.totalproducts;
          this.totalLoaded = response.headers.totalloaded;

          document.getElementById("grid").innerHTML = response.data;
        })
        .then(() => {
          this.currentPage = 1;
          this.showLoader = false;
        });
    },
    loadNextPage: function () {
      let pageNum = this.queryParams.get("page")
        ? parseInt(this.queryParams.get("page")) + 1
        : 2;

      this.updateParam("page", pageNum);

      // Load next page of products
      this.loading = true;
      this.load()
        .then((response) => {
          this.totalPages = response.headers.totalpages;
          this.totalProducts = response.headers.totalproducts;
          this.totalLoaded = response.headers.totalloaded;

          document
            .getElementById("grid")
            .insertAdjacentHTML("beforeend", response.data);
        })
        .then(() => {
          this.currentPage = pageNum;
          this.loading = false;
        });
    },
    load: function () {
      history.pushState(null, null, "?" + this.queryParams.toString());
      axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

      return axios.get(this.url + "?" + this.queryParams.toString());
    },
  },
  created() {
    this.currentPage = this.page;
    this.totalPages = this.total;
    this.totalProducts = this.results;
    this.totalLoaded = this.loaded;
    this.url = location.protocol + "//" + location.host + location.pathname;

    this.sortDirection = this.loadedDirection;
    this.searchTerm = this.queryParams.get("searchTerm");
    this.selectedSortOption = this.sortOptions.find((option) => {
      return option.code == this.loadedOption;
    });

    this.filterOptions.forEach((optionset) => {
      let params = this.queryParams.getAll(optionset.handle);

      if (params.length) {
        params = params[0].split(",");
        this.filters[optionset.handle] = optionset.options.filter((option) => {
          return params.find((param) => {
            return param == option.code;
          });
        });
      }
    });
  },
  mounted() {
    this.initial = false;
  },
};
</script>

<style lang="scss">
@import "../../scss/base/variables.scss";

.c-categoryGrid {
  position: relative;

  &__grid {
    display: flex;
    flex-wrap: wrap;
  }

  &__loadMore {
    .o-btn {
      position: relative;

      .o-loaderInline {
        display: none;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        margin-left: -22px;

        div {
          background: $c-white;
        }
      }

      &.loading {
        color: $brand-dark-blue;
        background: $brand-dark-blue;
        .o-loaderInline {
          display: inline-block;
        }
      }
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $overlay-blue;

    .o-loader div {
      background: $c-white;
    }
  }
}
</style>
